import { useParams } from 'react-router-dom';
import { type WebSection, useGetZoneDetail } from '@wbk/contentful/api';

import Error404 from '@/components/error/404';
import FullpageAnimatedLogo from '@/components/ui/Loading/FullpageAnimatedLogo';
import Seo from '@/components/headers/seo';
import Bento from '@/components/zones/bento';
import HeroSection from '@/components/sections/zones/Hero';

import ZonesProvider from '@/context/zones/provider';
import SponsorsSection from '@/components/sections/SponsorsSection';
import ZonesDetailsSections from '@/components/web-sections/ZonesDetailsSections';

const ZoneDetailPage = () => {
  const { lang, slug } = useParams<{ lang: Language; slug: string }>();

  const { data, isLoading } = useGetZoneDetail({
    lang,
    limit: 1,
    where: { slug },
  });

  const zone = data?.data;
  const webSections = zone?.webSectionsCollection?.items.filter(
    (x) => x?.__typename === 'WebSection'
  ) as WebSection[];
  const anchors = (webSections ?? [])?.find((x) => x?.renderType === 'anchors');
  if (isLoading) {
    return <FullpageAnimatedLogo />;
  }

  if (!zone) {
    return <Error404 />;
  }

  return (
    <>
      <Seo
        title={zone?.title}
        description={zone?.description}
        image={zone?.imageBg?.url || zone?.featuredBanner?.url}
      />
      <ZonesProvider anchors={anchors}>
        <div className='mb-20 flex flex-col gap-12 lg:gap-20'>
          <div className='flex flex-col gap-12'>
            <HeroSection section={zone} />

            <Bento zone={zone} />
          </div>

          <div className='mt-12 flex flex-col gap-12 lg:gap-32'>
            {zone?.webSectionsCollection?.items.map((section, idx) => {
              if (section?.__typename !== 'WebSection') {
                return null;
              }

              const firstItem = (section as WebSection)?.contentCollection?.items?.[0];
              const hasSponsorSection = firstItem?.__typename === 'SponsorList';

              if (hasSponsorSection) {
                return (
                  <SponsorsSection
                    key={`sponsors-${idx}`}
                    section={section}
                    className='container border-none'
                  />
                );
              }

              // Handle regular WebSection
              return <ZonesDetailsSections key={`section-${idx}`} section={section} />;
            })}
          </div>
        </div>
      </ZonesProvider>
    </>
  );
};

export default ZoneDetailPage;
